import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useBulkAssignSeeds } from '@/hooks/use-seeds';
import { useSearchStudents } from '@/hooks/use-admin';
import { toToastMessage } from '@/lib/api-error';
import type { StudentSearchItem } from '@/types/admin';

export interface BulkAssignStudentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSeedIds: string[];
  onSuccess: () => void;
}

function formatStudentName(s: StudentSearchItem): string {
  const name = [s.first_name, s.last_name].filter(Boolean).join(' ').trim();
  return name || s.username || s.email || s.id.slice(0, 8);
}

export function BulkAssignStudentsDialog({
  open,
  onOpenChange,
  selectedSeedIds,
  onSuccess,
}: BulkAssignStudentsDialogProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<StudentSearchItem[]>([]);
  const [dueDate, setDueDate] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useBulkAssignSeeds();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Reset when closed.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedStudents([]);
      setDueDate('');
    }
  }, [open]);

  const { data: searchResults, isLoading: isSearching } = useSearchStudents(debouncedQuery);

  const selectedIdSet = new Set(selectedStudents.map((s) => s.id));
  const filteredResults = (searchResults ?? []).filter((s) => !selectedIdSet.has(s.id));

  const addStudent = (s: StudentSearchItem) => {
    setSelectedStudents((prev) => [...prev, s]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeStudent = (id: string) => {
    setSelectedStudents((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSubmit = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Select at least one student');
      return;
    }
    try {
      const result = await mutation.mutateAsync({
        seed_ids: selectedSeedIds,
        student_ids: selectedStudents.map((s) => s.id),
        due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
      });
      const parts = [`Created ${result.created} assignment${result.created === 1 ? '' : 's'}`];
      if (result.skipped > 0) parts.push(`${result.skipped} skipped (duplicates)`);
      if (result.failed > 0) parts.push(`${result.failed} failed`);
      toast.success(parts.join(', '));
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(toToastMessage(e, 'Bulk assign failed'));
    }
  };

  const totalAssignments = selectedSeedIds.length * selectedStudents.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign {selectedSeedIds.length} seeds to students</DialogTitle>
          <DialogDescription>
            Every selected seed will be assigned to every selected student. Duplicates are
            skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Students</Label>

            {selectedStudents.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedStudents.map((s) => (
                  <span
                    key={s.id}
                    className="inline-flex items-center gap-1 rounded-full border bg-accent/50 px-2.5 py-1 text-xs"
                  >
                    <span className="font-medium">{formatStudentName(s)}</span>
                    <button
                      type="button"
                      className="rounded-full p-0.5 hover:bg-accent"
                      onClick={() => removeStudent(s.id)}
                      aria-label={`Remove ${formatStudentName(s)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or username..."
                className="pl-9"
              />
              {isSearching && (
                <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {debouncedQuery.length >= 2 && (
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {filteredResults.length > 0 ? (
                  filteredResults.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => addStudent(s)}
                    >
                      <div className="flex-1">
                        <div className="font-medium">{formatStudentName(s)}</div>
                        {(s.username || s.telegram_id) && (
                          <div className="text-xs text-muted-foreground">
                            {s.username ? `@${s.username}` : `ID: ${s.telegram_id}`}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                ) : !isSearching ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    {searchResults && searchResults.length > 0
                      ? 'All matching students already selected'
                      : 'No students found'}
                  </div>
                ) : null}
              </div>
            )}
            {debouncedQuery.length > 0 && debouncedQuery.length < 2 && (
              <p className="text-xs text-muted-foreground">Type at least 2 characters to search</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bulkAssignDueDate">Due date (optional)</Label>
            <Input
              id="bulkAssignDueDate"
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          {selectedStudents.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Will create up to {totalAssignments} assignment{totalAssignments === 1 ? '' : 's'} (
              {selectedSeedIds.length} × {selectedStudents.length}).
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending || selectedStudents.length === 0}
          >
            {mutation.isPending ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
